import { Button } from '../../components/Button';
import { Drawer } from '../../components/Drawer';
import { BasketContents } from '../../composites/BasketContents';
import { Recommendations } from '../../composites/Recommendations';
import { ChevronRightIcon } from '../../icons/ChevronRightIcon';
import { space } from '../../theme/tokens';
import {
  ActionBar,
  ActionRow,
  Body,
  ContinueButton,
  ContinueIcon,
  RecommendationsSection,
} from './BasketDrawerTemplate.styles';
import type { BasketDrawerLabels, BasketDrawerTemplateProps } from './BasketDrawerTemplate.types';

const DEFAULT_LABELS: Required<BasketDrawerLabels> = {
  title: 'Your basket',
  closeLabel: 'Close basket',
  addedConfirmation: 'Added to basket',
  itemisedView: 'Itemised view',
  itineraryView: 'Itinerary view',
  total: 'Total',
  viewBasket: 'View Basket',
  checkout: 'Checkout',
  continueShopping: 'Continue shopping',
  removeLabel: 'Remove',
  removing: 'Removing…',
  recommendationsHeadingPrimary: 'Wait.',
  recommendationsHeadingSecondary: "Don't forget to make plans...",
  recommendationsAdd: 'Add',
  selectGuestLabel: 'Select guest',
  noImageLabel: 'No image',
};

export function BasketDrawerTemplate({
  open,
  onClose,
  items,
  totalPrice,
  itemCount,
  view = 'itemised',
  onViewChange,
  guests,
  onRemoveItem,
  removingItemId = null,
  isUpdating = false,
  emptyMessage,
  showAddedConfirmation = false,
  recommendations,
  onRecommendationSelect,
  onViewBasket,
  onCheckout,
  isCheckoutBlocked = false,
  onContinueShopping,
  locale,
  labels,
}: BasketDrawerTemplateProps) {
  const resolvedLabels = { ...DEFAULT_LABELS, ...labels };
  const recs = recommendations ?? [];
  const showRecommendations = recs.length > 0;

  const footer = (
    <ActionBar>
      <ActionRow>
        <Button variant="secondary" fullWidth onClick={onViewBasket}>
          {`${resolvedLabels.viewBasket} (${itemCount})`}
        </Button>
        <Button
          variant="primary"
          fullWidth
          onClick={onCheckout}
          disabled={isUpdating || itemCount === 0 || isCheckoutBlocked}
        >
          {resolvedLabels.checkout}
        </Button>
      </ActionRow>
      <ContinueButton type="button" onClick={onContinueShopping}>
        {resolvedLabels.continueShopping}
        <ContinueIcon>
          <ChevronRightIcon size={12} />
        </ContinueIcon>
      </ContinueButton>
    </ActionBar>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      aria-label={resolvedLabels.title}
      closeLabel={resolvedLabels.closeLabel}
      width="600px"
      mobileTopOffset={space['space-1000']}
      backdropBlur
      footer={footer}
    >
      <Body data-testid="basket-drawer-template">
        <BasketContents
          items={items}
          totalPrice={totalPrice}
          view={view}
          onViewChange={onViewChange}
          guests={guests}
          onRemoveItem={onRemoveItem}
          removingItemId={removingItemId}
          isUpdating={isUpdating}
          emptyMessage={emptyMessage}
          showAddedConfirmation={showAddedConfirmation}
          locale={locale}
          labels={{
            title: resolvedLabels.title,
            itemisedView: resolvedLabels.itemisedView,
            itineraryView: resolvedLabels.itineraryView,
            addedConfirmation: resolvedLabels.addedConfirmation,
            total: resolvedLabels.total,
            removeLabel: resolvedLabels.removeLabel,
            removing: resolvedLabels.removing,
            noImageLabel: resolvedLabels.noImageLabel,
            selectGuestLabel: resolvedLabels.selectGuestLabel,
          }}
        />

        {showRecommendations && (
          <RecommendationsSection>
            <Recommendations
              items={recs}
              onSelect={onRecommendationSelect}
              title={resolvedLabels.recommendationsHeadingPrimary}
              titleSecondary={resolvedLabels.recommendationsHeadingSecondary}
              noImageLabel={resolvedLabels.noImageLabel}
              actionLabel={resolvedLabels.recommendationsAdd}
              cardSize="small"
              layout="grid"
              imageAspectRatio="16 / 9"
              imageMobileAspectRatio="4 / 4.6"
              fillLastRowWhenOdd
              rowGap={space['space-1000']}
            />
          </RecommendationsSection>
        )}
      </Body>
    </Drawer>
  );
}
